import { equalLengthAndValues, vecFromLatLong } from '@core/logic';
import { useGetPhotoListQuery } from '@core/redux/query';
import { Photo } from '@photos/types';
import type { ScanFilters } from '@projects/pages/detail/hooks';
import { useThree } from '@react-three/fiber';
import { getRenderOrder } from '@viewer3D/helper';
import { Object3dNames } from '@viewer3D/types';
import { useCallback, useEffect, useMemo } from 'react';
import { Camera, Vector3 } from 'three';
import { useSnapshot } from 'valtio';
import { PhotoIcon } from './PhotoIcon';
import {
  photoSelectionBoundingBoxState,
  photosState,
  setSelectedPhotos,
  setPhotoSelectionBoundingBox,
  togglePhotos,
  type Snapshot,
} from './state';
import { useConnect } from './useConnect';

const DATA_KEEP_ALIVE_MS = 1000 * 60; // 1 minute

function worldToScreen(
  worldPosition: Vector3,
  camera: Camera,
  rendererDomElement: HTMLCanvasElement,
  canvasRect?: DOMRect,
) {
  // Step 1: Project world position to normalized device coordinates (NDC)
  const vector = worldPosition.clone().project(camera); // NDC: [-1, 1]

  // Step 2: Convert NDC to screen (pixel) coordinates relative to canvas
  const widthHalf = 0.5 * rendererDomElement.clientWidth;
  const heightHalf = 0.5 * rendererDomElement.clientHeight;

  const rect = canvasRect || rendererDomElement.getBoundingClientRect();

  const x = vector.x * widthHalf + widthHalf + rect.left;
  const y = -vector.y * heightHalf + heightHalf + rect.top;

  return { x, y, z: vector.z };
}

export const PhotoIcons = (
  props: Pick<ScanFilters, 'dateRange' | 'viewport'> & { projectId: string },
) => {
  const {
    showPhotos,
    photosInViewport: { singles, clusters },
    cameraViewport,
    projectId,
    handlePhotosForProject,
  } = useConnect(props);
  const { camera, gl } = useThree();

  const { boundingBox: selectionBoundingBox } = useSnapshot(photoSelectionBoundingBoxState);
  const { selectedPhotos } = useSnapshot(photosState);

  const isPhotoSelected = useCallback(
    (photo: Snapshot<Photo> | Photo, canvasRect?: DOMRect) => {
      if (!selectionBoundingBox || !cameraViewport) return false;

      const photoPosition = vecFromLatLong(photo.lat, photo.long, cameraViewport.z);
      const screenPosition = worldToScreen(photoPosition, camera, gl.domElement, canvasRect);

      if (screenPosition.z > 1) return false;

      const isSelected =
        screenPosition.x >= selectionBoundingBox.minX &&
        screenPosition.x <= selectionBoundingBox.maxX &&
        screenPosition.y >= selectionBoundingBox.minY &&
        screenPosition.y <= selectionBoundingBox.maxY;

      return isSelected;
    },
    [selectionBoundingBox, cameraViewport, camera, gl],
  );

  useEffect(() => {
    // If singles, clusters or cameraViewport changed, clear the selection box
    setPhotoSelectionBoundingBox({ minX: 0, minY: 0, maxX: 0, maxY: 0 });
  }, [singles, clusters, cameraViewport]);

  useEffect(() => {
    if (!showPhotos) {
      if (selectedPhotos.length > 0) {
        setSelectedPhotos([]);
      }
      return;
    }

    if (!selectionBoundingBox || selectionBoundingBox.minX === 0) return;

    const canvasRect = gl.domElement.getBoundingClientRect();

    const newSelectedPhotos = [...singles, ...clusters.flat()]
      .filter((p) => isPhotoSelected(p, canvasRect))
      .map((p) => photosState.allPhotos.find((allP) => allP.id === p.id)!)
      .filter(Boolean);

    const newSelectedPhotoIds = newSelectedPhotos.map((p) => p.id);
    const selectedPhotoIds = selectedPhotos.map((p) => p.id);

    if (equalLengthAndValues(newSelectedPhotoIds, selectedPhotoIds)) {
      return;
    }

    setSelectedPhotos(newSelectedPhotos);
  }, [showPhotos, selectionBoundingBox, singles, clusters, selectedPhotos, isPhotoSelected]);

  const {
    data: photoListData,
    refetch,
    fulfilledTimeStamp,
  } = useGetPhotoListQuery(
    { projectId, viewport: showPhotos ? cameraViewport : undefined },
    { skip: !showPhotos },
  );

  useEffect(() => {
    if (photoListData) {
      handlePhotosForProject(photoListData);
    }
  }, [photoListData, handlePhotosForProject]);

  useEffect(() => {
    if (!fulfilledTimeStamp) {
      return;
    }
    // refetch if the data gets older than DATA_KEEP_ALIVE_MS
    if (Date.now() - fulfilledTimeStamp > DATA_KEEP_ALIVE_MS) {
      refetch();
    }
  }, [props.dateRange, fulfilledTimeStamp]);

  useEffect(() => {
    togglePhotos(false);
  }, [projectId]);

  // Create a Set of selected photo IDs for O(1) lookup performance
  const selectedPhotoIds = useMemo(() => {
    return new Set(selectedPhotos.map((photo) => photo.id));
  }, [selectedPhotos]);

  // Helper to check if a photo is selected - memoized for performance
  const isPhotoInSelection = useMemo(() => {
    return (photo: Snapshot<Photo> | Photo) => selectedPhotoIds.has(photo.id);
  }, [selectedPhotoIds]);

  // Helper to check if any photo in cluster is selected
  const isClusterSelected = useMemo(() => {
    return (cluster: readonly (Snapshot<Photo> | Photo)[]) => cluster.some(isPhotoInSelection);
  }, [isPhotoInSelection]);

  if (!showPhotos || !cameraViewport) return null;

  return (
    <>
      <group name={Object3dNames.Photos} renderOrder={getRenderOrder(Object3dNames.Photos)}>
        {clusters
          .filter((cluster) => cluster.length > 0)
          .map((cluster, index) => (
            <PhotoIcon
              key={`cluster-${index}-${cluster.map((p) => p.id).join('-')}`}
              photos={cluster}
              z={cameraViewport.z}
              isSelected={isClusterSelected(cluster)}
              projectId={projectId}
            />
          ))}
        {singles
          .filter((photo) => photo)
          .map((photo) => (
            <PhotoIcon
              key={photo.id}
              photo={photo}
              z={cameraViewport.z}
              isSelected={isPhotoInSelection(photo)}
              projectId={projectId}
            />
          ))}
      </group>
    </>
  );
};
